import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { NotificationSummaryProvider } from "../hooks/useNotificationSummary";
import { useGlobalShortcuts } from "../hooks/useGlobalShortcuts";
import { navItemsFor } from "../lib/nav";
import Sidebar from "./Sidebar";
import TopBar from "./TopBar";
import MobileTabBar from "./MobileTabBar";
import CommandPalette from "./CommandPalette";
import ShortcutsDialog from "./ShortcutsDialog";
import HelpCenter from "./HelpCenter";

const COLLAPSED_KEY = "sherlock_sidebar_collapsed";
// Tailwind's md breakpoint: from here the sidebar sits beside the page,
// below it the sidebar is a drawer over the page.
const DESKTOP_QUERY = "(min-width: 768px)";

function readCollapsed() {
  try {
    return localStorage.getItem(COLLAPSED_KEY) === "true";
  } catch {
    return false;
  }
}

function desktopNow() {
  if (typeof window === "undefined" || !window.matchMedia) return true;
  return window.matchMedia(DESKTOP_QUERY).matches;
}

function useIsDesktop() {
  const [desktop, setDesktop] = useState(desktopNow);
  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const mq = window.matchMedia(DESKTOP_QUERY);
    const onChange = () => setDesktop(mq.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  return desktop;
}

const Layout = () => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const desktop = useIsDesktop();
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);

  const toggleCollapsed = useCallback(() => {
    setCollapsed((c) => {
      try {
        localStorage.setItem(COLLAPSED_KEY, String(!c));
      } catch {
        // The choice just isn't remembered.
      }
      return !c;
    });
  }, []);
  const openDrawer = useCallback(() => setDrawerOpen(true), []);
  const closeDrawer = useCallback(() => setDrawerOpen(false), []);
  // A dialog opened from the drawer closes the drawer first, so focus is
  // not trapped behind it.
  const openPalette = useCallback(() => { setDrawerOpen(false); setPaletteOpen(true); }, []);
  const togglePalette = useCallback(() => { setDrawerOpen(false); setPaletteOpen((o) => !o); }, []);
  const openHelp = useCallback(() => { setDrawerOpen(false); setHelpOpen(true); }, []);
  const openShortcuts = useCallback(() => { setDrawerOpen(false); setShortcutsOpen(true); }, []);

  const allowed = useMemo(() => new Set(navItemsFor(user).map((i) => i.to)), [user]);
  const canOpen = useCallback((path) => allowed.has(path), [allowed]);
  useGlobalShortcuts({ onPalette: togglePalette, onShortcuts: openShortcuts, navigate, canOpen });

  // Opening a page, or widening the window past the breakpoint, closes the
  // drawer.
  useEffect(() => { setDrawerOpen(false); }, [pathname, desktop]);
  useEffect(() => {
    if (!drawerOpen) return undefined;
    const onKey = (e) => { if (e.key === "Escape") setDrawerOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [drawerOpen]);

  // A phone always gets the full menu in its drawer; collapsing is a
  // desktop choice.
  const narrow = desktop && collapsed;

  return (
    <NotificationSummaryProvider>
      <div className="flex h-[100dvh] overflow-hidden bg-slate-50 dark:bg-slate-950">
        <Sidebar
          collapsed={narrow}
          onToggleCollapsed={toggleCollapsed}
          drawer={!desktop}
          drawerOpen={drawerOpen}
          onCloseDrawer={closeDrawer}
          onOpenHelp={openHelp}
          onOpenShortcuts={openShortcuts}
        />
        {!desktop && drawerOpen && (
          <div
            className="fixed inset-0 z-30 bg-slate-950/60"
            onClick={closeDrawer}
            aria-hidden="true"
            data-testid="sidebar-backdrop"
          />
        )}
        {/* The margin is the sidebar's width: 256px open, 72px collapsed,
            none under the drawer. */}
        <div
          className={`flex-1 min-w-0 flex flex-col overflow-hidden transition-[margin] duration-300 ${
            narrow ? "md:ml-[72px]" : "md:ml-64"
          }`}
          id="main-content"
          data-testid="main-content"
        >
          <TopBar
            desktop={desktop}
            drawerOpen={drawerOpen}
            onOpenDrawer={openDrawer}
            onOpenPalette={openPalette}
            onOpenHelp={openHelp}
          />
          {/* On a phone the page keeps clear of the bottom tab bar. */}
          <main className={`flex-1 overflow-y-auto ${desktop ? "" : "pb-tabbar"}`} data-testid="page-scroll">
            <Outlet />
          </main>
          {!desktop && <MobileTabBar onOpenMore={openDrawer} moreOpen={drawerOpen} />}
        </div>
      </div>
      <CommandPalette
        open={paletteOpen}
        onOpenChange={setPaletteOpen}
        onOpenHelp={openHelp}
        onOpenShortcuts={openShortcuts}
      />
      <ShortcutsDialog open={shortcutsOpen} onOpenChange={setShortcutsOpen} />
      <HelpCenter open={helpOpen} onOpenChange={setHelpOpen} />
    </NotificationSummaryProvider>
  );
};

export default Layout;
